import React, { useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Services, Utils, Components, Logger, Types } from "@sendum/core";

import { VisibilityRootState } from "../store/VisibilityStore";
import { setSelectedSensor, setSensors } from "../store/VisibilityActions";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logger = new Logger("VisibilityGraphsLegend");

import classes from "./VisibilityGraphsLegend.module.scss";
import { AnyAction } from "redux";

interface ILegend {
	sensor: string;
	name: string;
	icon?: string;
	x: string;
	y: string;
}
interface IProps {
	maxSensors: number;
}

export const VisibilityGraphsLegend = ({ maxSensors }: IProps): JSX.Element => {
	logger.debug("render");
	const dispatch = useDispatch();
	const device = useSelector((state: VisibilityRootState) => state.Visibility.device);
	const sensors = useSelector((state: VisibilityRootState) => state.Visibility.sensors);
	const selectedSensor = useSelector((state: VisibilityRootState) => state.Visibility.selectedSensor);

	const sensorsCurrent = useRef<string[]>([]);
	const [legends, setLegends] = useState(new Array<ILegend>());
	const [yAxis, setYAxis] = useState(new Map<string, { min: number | null; max: number | null }>());
	const [selectedSensorConfig, setSelectedSensorConfig] = useState<ILegend>();

	const deviceConfig = useMemo<Map<string, Types.TDeviceAttribute> | undefined>(() => device?.getAttributesConfig(), [device]);

	const updateLegend = React.useCallback((values: { [key: string]: { x: number; y: number } }) => {
		setLegends(sensorsCurrent.current.map(sensor => ({
			sensor,
			name: deviceConfig?.get(sensor)?.displayName || sensor,
			x: values[sensor]?.x ? Utils.time.format(values[sensor].x, "MMMM D, YYYY HH:mm:ss") : "--",
			y: Utils.getSensorDisplayValue(values[sensor]?.y, deviceConfig?.get(sensor)),
			icon: deviceConfig?.get(sensor)?.icon,
		})));
	}, [deviceConfig]);

	const updateLegendYaxis = React.useCallback((values: { [key: string]: { min: number; max: number } }) => {
		if (deviceConfig) {
			const _yAxis = new Map<string, { min: number | null; max: number | null }>();
			const sensorsNames = deviceConfig.keys() ?? [];
			for (const sN of sensorsNames) {
				_yAxis.set(sN, {
					min: isNaN(values[sN]?.min) ? null : values[sN]?.min,
					max: isNaN(values[sN]?.max) ? null : values[sN]?.max,
				});
			}
			setYAxis(_yAxis);
		}
	}, [deviceConfig]);

	React.useEffect(() => {
		if (deviceConfig) {
			Services.EventEmitter.on("update_legend", (values: { [key: string]: { x: number; y: number } }) => updateLegend(values));
			Services.EventEmitter.on("update_legend_yaxis", (values: { [key: string]: { min: number; max: number } }) => updateLegendYaxis(values));
		}

		return () => {
			Services.EventEmitter.off("update_legend");
			Services.EventEmitter.off("update_legend_yaxis");
		};
	}, [updateLegend, updateLegendYaxis, deviceConfig]);

	React.useEffect(() => {
		const config = device?.getAttributesConfig();
		sensorsCurrent.current = [...sensors];
		setLegends(sensors.map(sensor => ({
			sensor,
			name: config?.get(sensor)?.displayName || sensor,
			x: "--",
			y: "--",
			icon: config?.get(sensor)?.icon,
		})));
	}, [sensors, device]);

	React.useEffect(() => {
		if (selectedSensor) {
			const index = legends.findIndex(legend => legend.sensor === selectedSensor);
			setSelectedSensorConfig(legends[index]);
		}
	}, [selectedSensor, legends]);

	const getYaxisValue = (value: number | undefined | null): string => {
		const defaultValue = "";
		if (value === undefined || value === null) {
			return defaultValue;
		}
		if (value > 10000 || value < -10000) {
			return `${(value / 1000).toFixed(0)}k`;
		}

		return value.toString();
	};

	const sensorOnClickHandler = (sensorName: string): void => {
		dispatch(setSelectedSensor(sensorName) as unknown as AnyAction);
		if (sensorName) {
			dispatch(setSensors([sensorName]) as unknown as AnyAction);
		}
	};

	return (
		<div className={classes.graphsLegend__legend}>
			<div className={classes.graphsLegend__column}>
				<div className={classes.graphsLegend__sensors}>
					{!selectedSensor && new Array(maxSensors).fill(null).map((_, index) => (
						<div onClick={() => {
							sensorOnClickHandler(legends[index]?.sensor);
						}} className={[classes.graphsLegend__sensor, index ? classes.graphsLegend__border_top : ""].join(" ")} key={`legend${index}`}>

							<div className={classes.graphsLegend__sensor__noimg}>{legends[index]?.icon && (<img className={classes.graphsLegend__sensor__img} alt={""} src={Utils.sensorIcons.get(legends[index]?.icon as string) as string} />)} </div>

							<div className={classes.graphsLegend__sensor__present}>
								<div className={classes.graphsLegend__sensor__present__value}>
									{legends[index]?.y}
								</div>
								<div className={classes.graphsLegend__sensor__present__name}>
									{legends[index]?.name}
								</div>
								<div className={classes.graphsLegend__sensor__present__date}>
									{legends[index]?.x}
								</div>
							</div>
							<div className={classes.graphsLegend__sensor__yaxis}>
								<div
									className={classes.graphsLegend__sensor__yaxis__max}>{getYaxisValue(yAxis.get(legends[index]?.sensor)?.max)}</div>
								<div
									className={classes.graphsLegend__sensor__yaxis__min}>{getYaxisValue(yAxis.get(legends[index]?.sensor)?.min)}</div>
							</div>
						</div>
					))}
					{selectedSensor && selectedSensorConfig && (
						<div className={classes.graphsLegend__sensor} key={"legend" + selectedSensorConfig.sensor}>

							<div className={classes.graphsLegend__sensor__noimg}>{selectedSensorConfig.icon && (<img className={classes.graphsLegend__sensor__img} alt={""} src={Utils.sensorIcons.get(selectedSensorConfig.icon)} />)} </div>

							<div className={classes.graphsLegend__sensor__present}>
								<div className={classes.graphsLegend__sensor__present__value}>
									{selectedSensorConfig.y}
								</div>
								<div className={classes.graphsLegend__sensor__present__name}>
									{selectedSensorConfig.name}
								</div>
								<div className={classes.graphsLegend__sensor__present__date}>
									{selectedSensorConfig.x}
								</div>
							</div>
						</div>
					)}
				</div>
				<div className={[classes.graphsLegend__xaxis, selectedSensor ? "" : classes.graphsLegend__border_top_right].join(" ")}><Components.TimeZoneSelector disabled={true} /></div>
				<div className={[classes.graphsLegend__timeline, selectedSensor ? "" : classes.graphsLegend__border_top_right].join(" ")}>Shipment Timeline</div>
			</div>
		</div>
	);
};
